import jsPDF from 'jspdf';
import { Order, OrderProduct, Product } from '../types';
import { formatCurrency } from './helpers';
import { supabase } from './supabase';
import { calculateExpectedDeliveryDate } from '../utils/format';

// Format a date in the Dutch format DD-MM-YYYY
const formatDate = (date: string): string => {
  const d = new Date(date);
  return d.toLocaleDateString('nl-NL');
};

// Calculate the total amount for an order product
const calculateTotal = (product: OrderProduct): number => {
  return product.hoeveelheid * product.prijs_per_stuk;
};

// Calculate the total VAT for the order (21%)
const calculateVAT = (total: number): number => {
  return total * 0.21;
};

// Get the logo URL from Supabase storage
const getLogoUrl = async (): Promise<string | null> => {
  try {
    // Try to get the logo from the logos bucket (logo.png or logo.jpg)
    const { data: logoData, error: logoError } = await supabase
      .storage
      .from('logos')
      .list('', {
        limit: 10,
      });
    
    if (logoError) {
      console.error('Error fetching logo:', logoError);
      return null;
    }
    
    console.log('Available logo files:', logoData);
    
    // Look for logo files - prioritizing PNG, then JPG, then other formats
    const logoFile = logoData.find(file => file.name.toLowerCase() === 'repacked-logo.png') || 
                    logoData.find(file => file.name.toLowerCase() === 'repacked-logo.jpg') ||
                    logoData.find(file => file.name.toLowerCase().includes('screenshot')) ||
                    logoData.find(file => file.name.toLowerCase().includes('logo'));
    
    console.log('Selected logo file:', logoFile);
    
    if (logoFile) {
      const { data: url } = supabase
        .storage
        .from('logos')
        .getPublicUrl(logoFile.name);

      console.log('Generated logo URL:', url.publicUrl);
      return url.publicUrl;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting logo URL:', error);
    return null;
  }
};

// Add an image to the PDF
const addImageToPdf = async (doc: jsPDF, imageUrl: string, x: number, y: number, width: number): Promise<void> => {
  console.log('Attempting to add image to PDF from URL:', imageUrl);
  
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      
      img.onload = () => {
        console.log('Image loaded successfully. Dimensions:', img.width, 'x', img.height);
        
        // Calculate height maintaining aspect ratio
        const aspectRatio = img.height / img.width;
        const height = width * aspectRatio;
        
        console.log('Adding image to PDF with dimensions:', width, 'x', height, 'mm');
        
        try {
          // Convert image to data URL
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            console.error('Failed to get canvas context');
            reject(new Error('Failed to get canvas context'));
            return;
          }
          
          ctx.drawImage(img, 0, 0);
          
          const imageData = canvas.toDataURL('image/png');
          console.log('Image converted to data URL successfully');
          
          // Add to PDF
          doc.addImage(imageData, 'PNG', x, y, width, height);
          console.log('Image added to PDF successfully');
          resolve();
        } catch (canvasError) {
          console.error('Error processing image:', canvasError);
          reject(canvasError);
        }
      };
      
      img.onerror = (error) => {
        console.error('Error loading image:', error);
        reject(error);
      };
      
      console.log('Setting image source...');
      img.src = imageUrl;
    } catch (error) {
      console.error('Error in addImageToPdf:', error);
      reject(error);
    }
  });
};

/**
 * Generate a PDF order confirmation
 * @param order The order object
 * @param orderProducts The order products
 * @param companyInfo The company info (optional)
 * @returns The PDF as a Blob
 */
export const generateOrderConfirmationPDF = async (
  order: Order,
  orderProducts: OrderProduct[],
  companyInfo?: {
    name: string;
    address: string;
    postalCode: string;
    city: string;
    country: string;
    contactEmail: string;
    contactPhone: string;
  }
): Promise<Blob> => {
  // Create a new PDF document
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  // Set default font
  doc.setFont('helvetica');
  
  // Define RE-PACKED company info
  const rePacked = {
    name: 'RE-PACKED',
    address: 'Zure Vischsteeg 12',
    postalCode: '3143 BB',
    city: 'Maassluis',
    country: 'Nederland',
    contactEmail: 'info@re-packed.nl',
    contactPhone: '+31612962202',
  };
  
  // Use provided company info if available, otherwise use default RE-PACKED info
  const finalCompanyInfo = companyInfo || rePacked;
  
  // Hardcoded image URL as a fallback if dynamic loading fails
  const hardcodedLogoUrl = `https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png`;
  
  // ---- HEADER SECTION ----
  
  // Company info (left side)
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(finalCompanyInfo.name, 20, 20);
  doc.text(finalCompanyInfo.address, 20, 25);
  doc.text(`${finalCompanyInfo.postalCode} ${finalCompanyInfo.city}`, 20, 30);
  doc.text(finalCompanyInfo.country, 20, 35);
  doc.text(`Tel: ${finalCompanyInfo.contactPhone}`, 20, 40);
  doc.text(`Email: ${finalCompanyInfo.contactEmail}`, 20, 45);
  
  // Logo (right side)
  try {
    await addImageToPdf(doc, hardcodedLogoUrl, 150, 15, 40);
  } catch (error) {
    console.warn('Failed to add logo image:', error);
    // Fallback to text logo
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 19, 80); // Dark blue
    doc.text('RE', 160, 25);
    doc.setTextColor(255, 93, 0); // Orange
    doc.text('-', 173, 25);
    doc.setTextColor(0, 19, 80); // Dark blue
    doc.text('PACKED', 178, 25);
  }
  
  // Title
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('ORDERBEVESTIGING', 20, 65);
  doc.setFont('helvetica', 'normal');
  
  // Line under title
  doc.setDrawColor(200, 200, 200);
  doc.line(20, 68, 190, 68);
  
  // ---- CUSTOMER INFO SECTION ----
  
  // Column 1: Order details
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Bestelnummer:', 20, 80);
  doc.text('Datum:', 20, 85);
  
  if (order.eigen_referentie) {
    doc.text('Eigen referentie:', 20, 90);
  }
  
  if (order.gewenste_leverdatum) {
    doc.text('Gewenste leverdatum:', 20, order.eigen_referentie ? 95 : 90);
  }
  
  doc.text('Status:', 20, order.gewenste_leverdatum ? (order.eigen_referentie ? 100 : 95) : (order.eigen_referentie ? 95 : 90));
  
  // Column 1: Order details values
  doc.setFont('helvetica', 'normal');
  doc.text(order.ordernummer || order.id, 70, 80);
  doc.text(formatDate(order.aangemaakt_op), 70, 85);
  
  if (order.eigen_referentie) {
    doc.text(order.eigen_referentie, 70, 90);
  }
  
  if (order.gewenste_leverdatum) {
    doc.text(formatDate(order.gewenste_leverdatum), 70, order.eigen_referentie ? 95 : 90);
  }
  
  doc.text(order.status, 70, order.gewenste_leverdatum ? (order.eigen_referentie ? 100 : 95) : (order.eigen_referentie ? 95 : 90));
  
  // Column 2: Delivery address
  doc.setFont('helvetica', 'bold');
  doc.text('Bezorgadres:', 120, 80);
  
  doc.setFont('helvetica', 'normal');
  doc.text(order.bezorgadres, 120, 85);
  doc.text(`${order.bezorg_postcode} ${order.bezorg_stad}`, 120, 90);
  doc.text(order.bezorg_land || 'Nederland', 120, 95);
  
  // Add spacing before products table
  const tableStartY = order.gewenste_leverdatum ? (order.eigen_referentie ? 115 : 110) : (order.eigen_referentie ? 110 : 105);
  
  // ---- PRODUCTS TABLE SECTION ----
  
  // Table header background
  doc.setFillColor(240, 240, 240);
  doc.rect(20, tableStartY, 170, 8, 'F');
  
  // Table headers
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Product', 22, tableStartY + 5);
  doc.text('Aantal', 110, tableStartY + 5);
  doc.text('Prijs', 130, tableStartY + 5);
  doc.text('Totaal', 165, tableStartY + 5);
  
  // Table content
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  
  let yPos = tableStartY + 15;
  let totalAmount = 0;
  
  // Function to split text into multiple lines
  const splitText = (text: string, maxCharsPerLine: number): string[] => {
    if (!text) return [];
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    
    words.forEach(word => {
      if ((currentLine + word).length <= maxCharsPerLine) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    });
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines;
  };
  
  // Function to check if we need a new page
  const checkPageOverflow = (currentY: number, rowHeight: number) => {
    // Allow 40mm from the bottom for totals and footer (more conservative)
    if (currentY + rowHeight > 230) { // Reduced from 240 to 230 to start new page earlier
      doc.addPage();
      
      // Add header to new page
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      
      // Table header background
      doc.setFillColor(240, 240, 240);
      doc.rect(20, 20, 170, 8, 'F');
      
      doc.text('Product', 22, 25);
      doc.text('Aantal', 110, 25);
      doc.text('Prijs', 130, 25);
      doc.text('Totaal', 165, 25);
      
      // Return position below the header with more margin
      return 40; // Increased from 35 to 40 for more spacing after headers
    }
    return currentY;
  };
  
  // Loop through products
  orderProducts.forEach((product, index) => {
    const productTotal = calculateTotal(product);
    totalAmount += productTotal;
    
    const productData = product.product || { 
      naam: '', 
      artikelnummer: '', 
      beschrijving: ''
    };
    
    // Get product info
    const productName = productData.naam || `Product ${index + 1}`;
    const artikelnummer = productData.artikelnummer || '';
    const description = productData.beschrijving || '';
    const afmetingen = (productData as any).afmetingen || '';
    const palletisering = (productData as any).palletisering || '';
    
    // Product notes
    const productNotes = (product as any).notities;
    
    // Build description block with proper spacing between sections
    const descriptionParts = [];
    
    // Add artikelnummer if available
    if (artikelnummer) {
      descriptionParts.push(`Art.nr: ${artikelnummer}`);
    }
    
    // Add afmetingen if available
    if (afmetingen) {
      descriptionParts.push(`Afmetingen: ${afmetingen}`);
    }
    
    // Add description if available
    if (description) {
      descriptionParts.push(description);
    }
    
    // Add palletisering if available
    if (palletisering) {
      descriptionParts.push(`Palletisering: ${palletisering}`);
    }
    
    // Add notes if available
    if (productNotes) {
      descriptionParts.push(`Notities: ${productNotes}`);
    }
    
    // Calculate row height based on content
    // Split into lines with proper spacing
    const productLines = productName.length > 35 ? 
      splitText(productName, 35) : 
      [productName];

    // Get actual description lines with proper text wrapping
    const wrappedDescriptionLines: string[] = [];

    // Process and wrap each description part properly with better spacing
    descriptionParts.forEach((part, partIndex) => {
      if (part.trim() !== '') {
        // Split long lines into multiple lines
        const partLines = splitText(part, 60); // Increased from 40 to allow more text per line
        partLines.forEach(line => wrappedDescriptionLines.push(line));
        
        // Add a blank line after each part except the last one
        if (partIndex < descriptionParts.length - 1) {
          wrappedDescriptionLines.push('');
        }
      }
    });

    // Remove the last blank line if it exists
    if (wrappedDescriptionLines.length > 0 && wrappedDescriptionLines[wrappedDescriptionLines.length - 1] === '') {
      wrappedDescriptionLines.pop();
    }

    // Calculate exact row height needed based on actual content - with more padding
    const rowHeight = 5 + (productLines.length * 4) + (wrappedDescriptionLines.length * 5); // Increased from 4 to 5 for line spacing

    // Check if we need a new page
    yPos = checkPageOverflow(yPos, rowHeight + 5); // Increased padding from 2 to 5

    // Alternating row background
    if (index % 2 === 1) {
      doc.setFillColor(248, 248, 248);
      doc.rect(20, yPos - 3, 170, rowHeight + 5, 'F'); // Increased padding from 2 to 5
    }

    // Product name (in bold)
    doc.setFont('helvetica', 'bold');
    let nameYPos = yPos;
    productLines.forEach(line => {
      doc.text(line, 22, nameYPos);
      nameYPos += 4;
    });

    // Start description with more spacing below product name
    let descYPos = yPos + (productLines.length * 4) + 2; // Added 2 for more space after product name

    // Description and additional info with proper wrapping and improved spacing
    if (wrappedDescriptionLines.length > 0) {
      doc.setFont('helvetica', 'normal');
      wrappedDescriptionLines.forEach(line => {
        if (line !== '') { // Skip blank lines used as separators
          doc.text(line, 22, descYPos);
        }
        descYPos += 5; // Increased from 4 to 5 for better readability
      });
    }
    
    // Quantity, price and total
    doc.setFont('helvetica', 'normal');
    doc.text(product.hoeveelheid.toString(), 110, yPos);
    doc.text(formatCurrency(product.prijs_per_stuk), 130, yPos);
    doc.text(formatCurrency(productTotal), 165, yPos);
    
    // Move to next row with more padding
    yPos += rowHeight + 8; // Increased from 4 to 8 for more spacing between products
  });
  
  // ---- TOTALS SECTION ----
  
  // Line above totals
  doc.setDrawColor(200, 200, 200);
  doc.line(20, yPos, 190, yPos);
  yPos += 10;
  
  // Totals
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Subtotaal:', 130, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(formatCurrency(totalAmount), 165, yPos);
  
  yPos += 7;
  const vat = calculateVAT(totalAmount);
  doc.setFont('helvetica', 'bold');
  doc.text('BTW (21%):', 130, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(formatCurrency(vat), 165, yPos);
  
  yPos += 7;
  doc.setFont('helvetica', 'bold');
  doc.text('Totaal:', 130, yPos);
  doc.text(formatCurrency(totalAmount + vat), 165, yPos);
  
  // ---- NOTES SECTION ----
  
  if (order.notities) {
    // Check if adding notes would overflow page
    if (yPos + 25 > 250) { // 25mm is estimated height for notes
      doc.addPage();
      yPos = 30; // Start notes on new page
    }
    
    yPos += 15;
    doc.setFont('helvetica', 'bold');
    doc.text('Notities:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    
    const notesLines = splitText(order.notities, 80);
    notesLines.forEach(line => {
      yPos += 5;
      doc.text(line, 20, yPos);
    });
  }
  
  // ---- FOOTER SECTION ----
  
  doc.setFontSize(8);
  const footerY = 280;
  
  // Footer line
  doc.setDrawColor(200, 200, 200);
  doc.line(20, footerY - 10, 190, footerY - 10);
  
  // Footer text
  doc.setTextColor(100, 100, 100);
  doc.text('Heeft u vragen over de bestelling? Neem gerust contact met ons op via info@re-packed.nl of bel ons op +31612962202.', 20, footerY - 5);
  doc.text('RE-PACKED - Zure Vischsteeg 12 - 3143 BB - Maassluis - Nederland', 20, footerY);
  
  // Return the PDF as a blob
  return doc.output('blob');
};

/**
 * Generate a PDF purchase order for a specific supplier
 * @param order The order object
 * @param orderProducts The order products for this supplier
 * @param supplier The supplier information
 * @returns The PDF as a Blob
 */
export const generateSupplierPurchaseOrderPDF = async (
  order: Order,
  orderProducts: OrderProduct[],
  supplier: {
    name: string;
    contactPerson: string;
    email?: string;
    phone?: string;
  },
  customerCompany?: {
    name: string;
    contact_person?: string;
    email?: string;
    phone?: string;
  }
): Promise<Blob> => {
  // Create a new PDF document
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  // Set default font
  doc.setFont('helvetica');
  
  // Define RE-PACKED company info
  const rePacked = {
    name: 'RE-PACKED',
    address: 'Zure Vischsteeg 12',
    postalCode: '3143 BB',
    city: 'Maassluis',
    country: 'Nederland',
    contactEmail: 'info@re-packed.nl',
    contactPhone: '+31612962202',
  };
  
  // Hardcoded image URL as a fallback if dynamic loading fails
  const hardcodedLogoUrl = `https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png`;
  
  // ---- HEADER SECTION ----
  
  // RE-PACKED info (left side)
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(rePacked.name, 20, 20);
  doc.text(rePacked.address, 20, 25);
  doc.text(`${rePacked.postalCode} ${rePacked.city}`, 20, 30);
  doc.text(rePacked.country, 20, 35);
  doc.text(`Tel: ${rePacked.contactPhone}`, 20, 40);
  doc.text(`Email: ${rePacked.contactEmail}`, 20, 45);
  
  // Logo (right side)
  try {
    await addImageToPdf(doc, hardcodedLogoUrl, 150, 15, 40);
  } catch (error) {
    console.warn('Failed to add logo image:', error);
    // Fallback to text logo
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 19, 80); // Dark blue
    doc.text('RE', 160, 25);
    doc.setTextColor(255, 93, 0); // Orange
    doc.text('-', 173, 25);
    doc.setTextColor(0, 19, 80); // Dark blue
    doc.text('PACKED', 178, 25);
  }
  
  // Title
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('PURCHASE ORDER', 20, 65);
  doc.setFont('helvetica', 'normal');
  
  // Line under title
  doc.setDrawColor(200, 200, 200);
  doc.line(20, 68, 190, 68);
  
  // ---- SUPPLIER AND DELIVERY INFO SECTION ----
  
  // Supplier info (left)
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Supplier', 20, 80);
  doc.setFont('helvetica', 'normal');
  doc.text(supplier.name, 20, 85);
  doc.text(`Attn: ${supplier.contactPerson}`, 20, 90);
  if (supplier.email) {
    doc.text(`Email: ${supplier.email}`, 20, 95);
  }
  if (supplier.phone) {
    doc.text(`Tel: ${supplier.phone}`, 20, supplier.email ? 100 : 95);
  }
  
  // Delivery address (right)
  doc.setFont('helvetica', 'bold');
  doc.text('Delivery Address', 120, 80);
  doc.setFont('helvetica', 'normal');
  
  let deliveryY = 85;
  
  // Add customer company name if available
  if (customerCompany?.name) {
    doc.setFont('helvetica', 'bold');
    doc.text(customerCompany.name, 120, deliveryY);
    deliveryY += 5;
    
    // Add contact person if available
    if (customerCompany.contact_person) {
      doc.setFont('helvetica', 'normal');
      doc.text(`Attn: ${customerCompany.contact_person}`, 120, deliveryY);
      deliveryY += 5;
    }
    
    doc.setFont('helvetica', 'normal');
  }
  
  doc.text(order.bezorgadres, 120, deliveryY);
  doc.text(`${order.bezorg_postcode} ${order.bezorg_stad}`, 120, deliveryY + 5);
  doc.text(order.bezorg_land || 'Netherlands', 120, deliveryY + 10);
  
  // ---- ORDER DETAILS SECTION ----
  
  // Calculate detailsY based on customer company info
  let detailsY = 110;
  if (customerCompany?.name) {
    detailsY += 5; // Company name line
    if (customerCompany.contact_person) {
      detailsY += 5; // Contact person line
    }
  }
  
  // Order details (left column)
  doc.setFont('helvetica', 'bold');
  doc.text('Order Number:', 20, detailsY);
  if (order.eigen_referentie) {
    doc.text('Reference:', 20, detailsY + 6);
  }
  doc.text('Order Date:', 20, order.eigen_referentie ? detailsY + 12 : detailsY + 6);
  if (order.gewenste_leverdatum) {
    doc.text('Requested Delivery Date:', 20, order.eigen_referentie ? detailsY + 18 : detailsY + 12);
  }
  
  // Customer company info (right column)
  if (customerCompany?.name) {
    doc.setFont('helvetica', 'bold');
    doc.text('Customer:', 120, detailsY);
    doc.setFont('helvetica', 'normal');
    doc.text(customerCompany.name, 120, detailsY + 6);
    if (customerCompany.contact_person) {
      doc.text(`Contact: ${customerCompany.contact_person}`, 120, detailsY + 12);
    }
    if (customerCompany.email) {
      doc.text(`Email: ${customerCompany.email}`, 120, detailsY + 18);
    }
  }
  
  // Order details values (left column values)
  doc.setFont('helvetica', 'normal');
  doc.text(order.ordernummer || order.id, 70, detailsY);
  if (order.eigen_referentie) {
    doc.text(order.eigen_referentie, 70, detailsY + 6);
  }
  doc.text(formatDate(order.aangemaakt_op), 70, order.eigen_referentie ? detailsY + 12 : detailsY + 6);
  if (order.gewenste_leverdatum) {
    doc.text(formatDate(order.gewenste_leverdatum), 70, order.eigen_referentie ? detailsY + 18 : detailsY + 12);
  }
  
  // Add spacing before products table
  const tableStartY = detailsY + (order.gewenste_leverdatum ? (order.eigen_referentie ? 28 : 22) : (order.eigen_referentie ? 22 : 16));
  
  // ---- PRODUCTS TABLE SECTION ----
  
  // Table header background
  doc.setFillColor(240, 240, 240);
  doc.rect(20, tableStartY, 170, 8, 'F');
  
  // Table headers - Remove price columns
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Product', 22, tableStartY + 5);
  doc.text('Quantity', 150, tableStartY + 5);
  
  // Table content
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  
  let yPos = tableStartY + 15;
  let totalAmount = 0;
  
  // Function to split text into multiple lines
  const splitText = (text: string, maxCharsPerLine: number): string[] => {
    if (!text) return [];
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    
    words.forEach(word => {
      if ((currentLine + word).length <= maxCharsPerLine) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    });
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines;
  };

  // Modify the page overflow check function to match the new headers
  const checkPageOverflow = (currentY: number, rowHeight: number) => {
    // Allow 30mm from the bottom for footer (more conservative)
    if (currentY + rowHeight > 240) {
      doc.addPage();
      
      // Add header to new page
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      
      // Table header background
      doc.setFillColor(240, 240, 240);
      doc.rect(20, 20, 170, 8, 'F');
      
      doc.text('Product', 22, 25);
      doc.text('Quantity', 150, 25);
      
      // Return position below the header with margin
      return 35;
    }
    return currentY;
  };
  
  // Loop through products - modify to remove price rendering
  orderProducts.forEach((product, index) => {
    const productTotal = calculateTotal(product);
    totalAmount += productTotal;
    
    const productData = product.product || { 
      naam: '', 
      artikelnummer: '', 
      beschrijving: ''
    };
    
    // Get product info
    const productName = productData.naam || `Product ${index + 1}`;
    const artikelnummer = productData.artikelnummer || '';
    const description = productData.beschrijving || '';
    const afmetingen = (productData as any).afmetingen || '';
    const palletisering = (productData as any).palletisering || '';
    
    // Product notes
    const productNotes = (product as any).notities;
    
    // Build description block with improved spacing between sections
    const descriptionParts = [];
    
    // Add artikelnummer if available
    if (artikelnummer) {
      descriptionParts.push(`Art.No: ${artikelnummer}`);
    }
    
    // Add afmetingen if available
    if (afmetingen) {
      descriptionParts.push(`Dimensions: ${afmetingen}`);
    }
    
    // Add description if available
    if (description) {
      descriptionParts.push(description);
    }
    
    // Add palletisering if available
    if (palletisering) {
      descriptionParts.push(`Palletization: ${palletisering}`);
    }
    
    // Add notes if available
    if (productNotes) {
      descriptionParts.push(`Notes: ${productNotes}`);
    }
    
    // Calculate row height based on content
    // Split into lines with proper spacing
    const productLines = productName.length > 35 ? 
      splitText(productName, 35) : 
      [productName];

    // Get actual description lines with proper text wrapping
    const wrappedDescriptionLines: string[] = [];

    // Process and wrap each description part properly with better spacing
    descriptionParts.forEach((part, partIndex) => {
      if (part.trim() !== '') {
        // Add a label/header for each description part for better readability
        // Split long lines into multiple lines
        const partLines = splitText(part, 60); // Increased from 40 to allow more text per line
        partLines.forEach((line: string) => wrappedDescriptionLines.push(line));
        
        // Add a blank line after each part except the last one
        if (partIndex < descriptionParts.length - 1) {
          wrappedDescriptionLines.push('');
        }
      }
    });

    // Calculate exact row height needed based on actual content - add more padding
    const rowHeight = 5 + (productLines.length * 4) + (wrappedDescriptionLines.length * 5); // Increased from 4 to 5

    // Check if we need a new page
    yPos = checkPageOverflow(yPos, rowHeight + 5); // Increased from 2 to 5 for more spacing

    // Alternating row background
    if (index % 2 === 1) {
      doc.setFillColor(248, 248, 248);
      doc.rect(20, yPos - 3, 170, rowHeight + 5, 'F'); // Increased padding
    }

    // Product name (in bold)
    doc.setFont('helvetica', 'bold');
    let nameYPos = yPos;
    productLines.forEach(line => {
      doc.text(line, 22, nameYPos);
      nameYPos += 4;
    });

    // Start description with more spacing below product name
    let descYPos = yPos + (productLines.length * 4) + 2; // Added 2 for more space after product name

          // Description and additional info with proper wrapping and improved spacing
      if (wrappedDescriptionLines.length > 0) {
        doc.setFont('helvetica', 'normal');
        wrappedDescriptionLines.forEach((line: string) => {
          if (line !== '') { // Skip blank lines used as separators
            doc.text(line, 22, descYPos);
          }
          descYPos += 5; // Increased from 4 to 5 for better readability
        });
      }
    
    // Quantity only - no price or total
    doc.setFont('helvetica', 'normal');
    doc.text(product.hoeveelheid.toString(), 150, yPos);
    
    // Move to next row with more padding
    yPos += rowHeight + 8; // Increased from 4 to 8 for more spacing between products
  });
  
  // Remove totals section
  // Instead, add a simple line to close the table
  doc.setDrawColor(200, 200, 200);
  doc.line(20, yPos, 190, yPos);
  
  // ---- FOOTER SECTION ----
  
  doc.setFontSize(8);
  const footerY = 280;
  
  // Footer line
  doc.setDrawColor(200, 200, 200);
  doc.line(20, footerY - 10, 190, footerY - 10);
  
  // Important note in footer
  doc.setFont('helvetica', 'bold');
  doc.text('Please include our order and article numbers on all documents!', 20, footerY - 5);
  
  // Footer text
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.text('RE-PACKED - Zure Vischsteeg 12 - 3143 BB - Maassluis - Netherlands', 20, footerY);
  
  // Return the PDF as a blob
  return doc.output('blob');
};

/**
 * Save or open a PDF file
 * @param blob The PDF blob
 * @param filename The filename to save as
 */
export const savePDF = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

/**
 * Open a PDF in a new window
 * @param blob The PDF blob
 */
export const openPDF = (blob: Blob): void => {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  // URL.revokeObjectURL(url); // Don't revoke immediately as the window needs to load
};

/**
 * Generate supplier-specific purchase order PDFs for all suppliers in an order
 * @param order The order object
 * @param orderProducts All order products
 * @param suppliers Map of supplier IDs to supplier info
 * @returns Array of objects with supplier ID and PDF blob
 */
export const generateSupplierPurchaseOrders = async (
  order: Order,
  orderProducts: OrderProduct[],
  suppliers: {
    [key: string]: {
      id: string;
      name: string;
      contactPerson: string;
      email?: string;
      phone?: string;
    }
  },
  customerCompany?: {
    name: string;
    contact_person?: string;
    email?: string;
    phone?: string;
  }
): Promise<Array<{
  supplierId: string,
  supplierName: string,
  pdfBlob: Blob
}>> => {
  // Group order products by supplier
  const productsBySupplier: { [supplierId: string]: OrderProduct[] } = {};
  
  // Go through all order products and group them by supplier
  orderProducts.forEach(product => {
    if (product.product && product.product.leverancier_id) {
      const supplierId = product.product.leverancier_id;
      
      if (!productsBySupplier[supplierId]) {
        productsBySupplier[supplierId] = [];
      }
      
      productsBySupplier[supplierId].push(product);
    }
  });
  
  // Generate a PDF for each supplier
  const supplierPdfs = await Promise.all(
    Object.keys(productsBySupplier).map(async (supplierId) => {
      // Skip if we don't have supplier info
      if (!suppliers[supplierId]) {
        console.warn(`No supplier info found for supplier ID: ${supplierId}`);
        return null;
      }
      
      const supplierProducts = productsBySupplier[supplierId];
      const supplierInfo = suppliers[supplierId];
      
      // Generate the PDF for this supplier
      const pdfBlob = await generateSupplierPurchaseOrderPDF(
        order,
        supplierProducts,
        supplierInfo,
        customerCompany
      );
      
      return {
        supplierId,
        supplierName: supplierInfo.name,
        pdfBlob
      };
    })
  );
  
  // Filter out any null results (suppliers without info)
  return supplierPdfs.filter(result => result !== null) as Array<{
    supplierId: string,
    supplierName: string,
    pdfBlob: Blob
  }>;
};

/**
 * Save supplier purchase order PDFs to the Supabase storage
 * @param order The order object
 * @param supplierPDFs Array of supplier PDFs from generateSupplierPurchaseOrders
 * @returns Array of storage URLs
 */
export const saveSupplierPurchaseOrdersToStorage = async (
  order: Order,
  supplierPDFs: Array<{
    supplierId: string,
    supplierName: string,
    pdfBlob: Blob
  }>
): Promise<Array<{
  supplierId: string,
  supplierName: string,
  storageUrl: string
}>> => {
  const results = await Promise.all(
    supplierPDFs.map(async ({ supplierId, supplierName, pdfBlob }) => {
      // Use order number if available, otherwise use order id
      const orderIdentifier = order.ordernummer || order.id;
      
      // Create filename in the specified format: purchase-order-{ordernumber}-{supplierid}.pdf
      const filename = `purchase-order-${orderIdentifier}-${supplierId}.pdf`;
      
      // Path in storage bucket: orders/filename
      const filePath = `orders/${filename}`;
      
      try {
        // Upload the PDF to Supabase storage
        const { data, error } = await supabase
          .storage
          .from('order-pdfs')
          .upload(filePath, pdfBlob, {
            contentType: 'application/pdf',
            upsert: true, // Overwrite if file exists
          });
        
        if (error) {
          console.error(`Error saving purchase order PDF for supplier ${supplierName}:`, error);
          throw error;
        }
        
        // Get the public URL
        const { data: urlData } = supabase
          .storage
          .from('order-pdfs')
          .getPublicUrl(filePath);
        
        console.log(`Purchase order PDF saved for supplier ${supplierName} at ${urlData.publicUrl}`);
        
        return {
          supplierId,
          supplierName,
          storageUrl: urlData.publicUrl
        };
      } catch (error) {
        console.error(`Failed to save purchase order PDF for supplier ${supplierName}:`, error);
        throw error;
      }
    })
  );
  
  return results;
};

/**
 * Generate and save supplier purchase order PDFs for an order in one operation
 * @param order The order object
 * @param orderProducts All order products
 * @param suppliers Map of supplier IDs to supplier info
 * @returns Array of objects with supplier ID, name and storage URL
 */
export const generateAndSaveSupplierPurchaseOrders = async (
  order: Order,
  orderProducts: OrderProduct[],
  suppliers: {
    [key: string]: {
      id: string;
      name: string;
      contactPerson: string;
      email?: string;
      phone?: string;
    }
  },
  customerCompany?: {
    name: string;
    contact_person?: string;
    email?: string;
    phone?: string;
  }
): Promise<Array<{
  supplierId: string,
  supplierName: string,
  storageUrl: string
}>> => {
  try {
    // First generate all the PDFs
    const supplierPDFs = await generateSupplierPurchaseOrders(order, orderProducts, suppliers, customerCompany);
    
    if (supplierPDFs.length === 0) {
      console.log('No supplier purchase order PDFs were generated for order:', order.ordernummer || order.id);
      return [];
    }
    
    // Then save them to storage
    return await saveSupplierPurchaseOrdersToStorage(order, supplierPDFs);
    
  } catch (error) {
    console.error('Error generating and saving supplier purchase orders:', error);
    throw error;
  }
}; 